import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'

import {
  ExtendedUnitFilterOptions,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import FILTER_INITIAL_STATE from '@components/filter/v2/filterState'

import { BuildingInterface, Level } from '@api/building'
import { MappingBlockCollection } from '@api/interactive-plan'

import countFilterChanges from '@utilities/unit-filter-util'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import filterUnit from '@adUtilities/filter-unit'
import { Unit } from '@adUtilities/types/apartment'

import AddFilterButton from './add-filter-button'
import BackButton from './back-button'
import BlockDropdown from './block-dropdown'
import LevelHead from './level-head'
import NoUnitCard from './no-unit-card'
import UnitCard from './unit-card'

interface UnitsSidePanelProps {
  projectIdentity: ProjectIdentity
  building: BuildingInterface
  blocks: MappingBlockCollection
  unitFilter: ExtendedUnitFilterOptions
  filterPopupToggle: boolean
  firebaseActiveBlock: string
  firebaseActiveLevel: string
  firebaseActiveUnit: string
  setFilterPopupToggle: (arg: boolean) => void
  comparePropertyProps: ReturnType<typeof useCompareProperty>
}

function UnitsSidePanel({
  projectIdentity,
  building,
  blocks,
  unitFilter,
  filterPopupToggle,
  setFilterPopupToggle,
  firebaseActiveBlock,
  firebaseActiveLevel,
  firebaseActiveUnit,
  comparePropertyProps,
}: UnitsSidePanelProps) {
  const unitSidePanelContainerRef = useRef<HTMLDivElement>(null)

  const blockKeys: string[] =
    building?.blockOrders.length >= 1
      ? building?.blockOrders
      : Object.keys(blocks) || []

  const [filteredCount, setFilteredCount] = useState<number>(0)

  const filteredLevels = useMemo(() => {
    const levelList: Level[] = [...building.levels]

    const { apply: filterApplied, anyLevel } = unitFilter || {}

    let filterLevelList = levelList
      .map((unit) => ({
        ...unit,
        data:
          blockKeys.length > 1
            ? unit.data.filter((item) => item.blockId === firebaseActiveBlock)
            : unit.data,
      }))
      .filter((unit) => unit.data.length)

    if (!filterApplied) return filterLevelList

    if (!anyLevel) {
      filterLevelList = filterLevelList.filter(
        (item: Level) => item.level === firebaseActiveLevel
      )
    }

    const filteredLevelList: Level[] = []

    filterLevelList.forEach((item: Level) => {
      let units = item.data

      if (blockKeys.length > 1 && firebaseActiveBlock) {
        units = units.filter(
          (unit: Unit) => unit.blockId === firebaseActiveBlock
        )
      }

      units = unitFilter?.apply
        ? units.filter((unit: Unit) =>
            filterUnit(
              unit,
              unitFilter,
              projectIdentity?.showPrice,
              projectIdentity?.statusLabels?.available
            )
          )
        : units

      if (units.length > 0) {
        filteredLevelList.push({ ...item, data: units })
      }
    })

    return filteredLevelList
  }, [
    building.levels,
    blockKeys,
    firebaseActiveBlock,
    firebaseActiveLevel,
    unitFilter,
    projectIdentity?.showPrice,
    projectIdentity?.statusLabels?.available,
  ])

  useEffect(() => {
    if (unitFilter?.apply) {
      setFilteredCount(countFilterChanges(FILTER_INITIAL_STATE, unitFilter))
    } else {
      setFilteredCount(0)
    }
  }, [unitFilter])

  useEffect(() => {
    const timer = setTimeout(() => {
      const activeLevelElement = document.querySelector(
        '.active-level'
      ) as HTMLDivElement

      if (
        activeLevelElement &&
        unitSidePanelContainerRef?.current &&
        firebaseActiveLevel
      ) {
        const offsetTop =
          activeLevelElement.offsetTop -
          unitSidePanelContainerRef.current.offsetTop

        unitSidePanelContainerRef.current.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [firebaseActiveLevel, unitFilter?.apply])

  return (
    <div className="h-full w-full bg-[#EEEEEE]">
      <div className="flex flex-col gap-2 bg-white p-4">
        <div className="flex justify-between gap-4">
          <BackButton />
          <AddFilterButton
            unitCount={filteredCount}
            popupState={filterPopupToggle}
            togglePopup={setFilterPopupToggle}
          />
        </div>

        {blockKeys.length > 1 && (
          <BlockDropdown blocks={blockKeys} levels={building.levels} />
        )}
      </div>
      <div
        ref={unitSidePanelContainerRef}
        className="custom-scrollbar h-unit-sidepanel-container overflow-y-scroll px-4"
        style={
          blockKeys.length > 1 ? { height: 'calc(100vh - 162px)' } : undefined
        }
      >
        {filteredLevels.length > 0 ? (
          filteredLevels.map((level: Level) => (
            <div
              key={level.level}
              className={`${
                level.level === firebaseActiveLevel ? 'active-level' : ''
              }`}
            >
              <LevelHead
                level={level.level}
                active={level.level === firebaseActiveLevel}
              />
              {level.data.length > 0 ? (
                level.data.map((unit: Unit) => (
                  <UnitCard
                    key={unit.id}
                    unit={unit}
                    active={firebaseActiveUnit === unit.name}
                    comparePropertyProps={comparePropertyProps}
                  />
                ))
              ) : (
                <NoUnitCard />
              )}
            </div>
          ))
        ) : (
          <NoUnitCard />
        )}
      </div>
    </div>
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(UnitsSidePanel)
